import React, { forwardRef, useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { useOrgData } from '../../contexts/OrgDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { DivisionKey, ExecutiveFunction } from '../../types/orgchart.types';
import { InlineEditPopover, EditingState, DivisionInlineEdit, DepartmentInlineEdit, SectionInlineEdit, PostInlineEdit } from './InlineEditPopover';
import './WiseBoardView.css';

interface LineConnection {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  color: string;
  level?: number; // For routing at different heights to avoid crossing
}

// D3 link generator with smooth bump curve for cleaner vertical connections
const linkGenerator = d3.link<any, { x: number; y: number }>(d3.curveBumpY)
  .x(d => d.x)
  .y(d => d.y);

interface WiseBoardViewProps {
  textSize?: number; // percentage: 100 = default
  divisionWidth?: number; // percentage: 100 = default (6in)
  zoom?: number; // used to trigger connector recalculation
  editMode?: boolean; // show/hide edit actions on the board
  showFunctionDetails?: boolean; // show/hide function objectives
  showFunctions?: boolean; // show/hide functions within sections
  showSections?: boolean; // show/hide sections within departments
}

export const WiseBoardView = forwardRef<HTMLDivElement, WiseBoardViewProps>(({ textSize = 100, divisionWidth = 100, zoom = 1, editMode = true, showFunctionDetails = true, showFunctions = true, showSections = true }, ref) => {
  const { orgData, hiddenDivisions, getEmployeeById, focusedDivision, focusedDepartment, reorderFunction, reorderSection } = useOrgData();
  const { organization } = useAuth();
  const { t, language } = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<LineConnection[]>([]);
  const [editing, setEditing] = useState<EditingState | null>(null);

  // Handle click to edit - calculates position for popover
  const handleEditClick = useCallback((
    e: React.MouseEvent,
    type: 'division' | 'department' | 'section' | 'function' | 'executive' | 'new-department' | 'new-section' | 'new-function',
    divisionKey: DivisionKey,
    deptIndex?: number,
    sectionIndex?: number,
    funcIndex?: number,
    execId?: string
  ) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const popoverWidth = 320;
    const isMobile = window.innerWidth < 768;

    // Position popover - center on mobile, to the side on desktop
    let left: number;
    let top: number;

    if (isMobile) {
      // On mobile, center the popover horizontally
      left = Math.max(10, (window.innerWidth - popoverWidth) / 2);
      // Position below the element
      top = Math.min(rect.bottom + 10, window.innerHeight - 400);
    } else {
      // On desktop, position to the left of the clicked element (RTL layout)
      left = rect.left - popoverWidth - 20;
      top = rect.top;

      // If too close to left edge, position to the right instead
      if (left < 20) {
        left = rect.right + 20;
      }

      // If would go off right edge, adjust
      if (left + popoverWidth > window.innerWidth - 20) {
        left = window.innerWidth - popoverWidth - 20;
      }
    }

    // Ensure top is within viewport
    if (top < 100) {
      top = 100;
    }
    if (top > window.innerHeight - 300) {
      top = Math.max(100, window.innerHeight - 400);
    }

    const position = { top, left };

    setEditing({
      type,
      divisionKey,
      deptIndex,
      sectionIndex,
      funcIndex,
      execId,
      position
    });
  }, []);

  // Get executive functions (CEO, C-levels, Owner are now functions, not employees)
  const execFunctions = orgData.executiveFunctions || [];
  const ownerFunc = execFunctions.find(f => f.id === 'exec-owner');
  const ceoFunc = execFunctions.find(f => f.id === 'exec-ceo');
  const ceoFuncId = ceoFunc?.id;

  // Get assigned employee for a function (first one for backwards compatibility)
  const getAssignedEmployee = (funcId: string) => {
    const employeeIds = orgData.assignments?.[funcId] || [];
    if (employeeIds.length > 0) {
      return getEmployeeById(employeeIds[0]);
    }
    return null;
  };

  // Get all assigned employee names for display (joined with comma)
  const getAssignedNames = (funcId: string) => {
    const employeeIds = orgData.assignments?.[funcId] || [];
    const names = employeeIds
      .map(id => getEmployeeById(id)?.name)
      .filter(Boolean);
    return names.join(', ');
  };

  // Get assigned employee name for display (kept for backwards compatibility)
  const getAssignedName = (funcId: string) => {
    return getAssignedNames(funcId);
  };

  // Find C-level functions (functions that manage divisions)
  const cLevelFunctions = execFunctions.filter(f =>
    f.id !== 'exec-owner' &&
    f.id !== 'exec-ceo' &&
    f.managesDivisions &&
    f.managesDivisions.length > 0
  ).sort((a, b) => {
    // First, sort by sortOrder if available
    const orderA = a.sortOrder ?? Infinity;
    const orderB = b.sortOrder ?? Infinity;
    if (orderA !== orderB) {
      return orderA - orderB; // Ascending by sortOrder
    }
    // Fall back to sorting by max division number - in RTL, first item appears on right
    const getMaxDivNum = (func: ExecutiveFunction) => {
      if (func.managesDivisions && func.managesDivisions.length > 0) {
        return Math.max(...func.managesDivisions.map(d => orgData.divisions[d]?.number || 0));
      }
      return 0;
    };
    return getMaxDivNum(b) - getMaxDivNum(a); // Descending: 712 first (right), then 3456 (left)
  });

  // Division order for WISE board: 6, 5, 4, 3, 2, 1, 7
  const divisionOrder: DivisionKey[] = Object.entries(orgData.divisions)
    .sort((a, b) => {
      const numA = a[1].number === 7 ? -1 : a[1].number;
      const numB = b[1].number === 7 ? -1 : b[1].number;
      return numB - numA;
    })
    .map(([key]) => key as DivisionKey);

  // Get C-level function IDs
  const cLevelFuncIds = cLevelFunctions.map(f => f.id);

  // Get division manager - from explicit managerId on division
  const getDivisionManager = (divisionKey: DivisionKey) => {
    const division = orgData.divisions[divisionKey];

    // Check if division has an explicit managerId assigned
    if (division?.managerId) {
      const manager = orgData.employees.find(e => e.id === division.managerId);
      if (manager) return manager;
    }

    return undefined;
  };

  // Get which C-level function manages a division
  const getDivisionExecutiveFunc = (divisionKey: DivisionKey): ExecutiveFunction | undefined => {
    return cLevelFunctions.find(f =>
      f.managesDivisions && f.managesDivisions.includes(divisionKey)
    );
  };

  // Get department manager name - only from explicit managerId assignment
  const getDeptManager = (divisionKey: DivisionKey, deptNum: number) => {
    const division = orgData.divisions[divisionKey];
    const dept = division?.departments?.find(d => d.number === deptNum);

    // Only use explicit managerId assignment
    if (dept?.managerId) {
      const manager = orgData.employees.find(e => e.id === dept.managerId);
      if (manager) return manager.name;
    }

    return '';
  };

  const visibleDivisions = divisionOrder.filter(key =>
    !hiddenDivisions.has(key) &&
    (focusedDivision === null || key === focusedDivision)
  );

  // Filter C-level functions to only show those managing visible divisions
  const visibleCLevelFunctions = focusedDivision === null
    ? cLevelFunctions
    : cLevelFunctions.filter(func => {
        // Check if this C-level manages the focused division
        if (func.managesDivisions && func.managesDivisions.includes(focusedDivision)) return true;
        return false;
      });

  // Check if we're in focused mode (single division view)
  const isFocusedMode = focusedDivision !== null;

  // Calculate lines after render
  useEffect(() => {
    const calculateLines = () => {
      if (!containerRef.current) return;

      const newLines: LineConnection[] = [];
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();

      // With transform: scale(zoom), getBoundingClientRect returns screen coordinates.
      // But the SVG is inside the scaled container, so its coordinate space is unscaled.
      // We need to convert screen coords to SVG coords by dividing by zoom.
      const zoomFactor = zoom || 1;
      const toSvgCoord = (screenCoord: number) => screenCoord / zoomFactor;

      // Get Owner box
      const ownerBox = container.querySelector('[data-exec-id="exec-owner"]');

      // Get CEO box for reference
      const ceoBox = container.querySelector(`[data-exec-id="${ceoFuncId}"]`);
      if (!ceoBox) return;
      const ceoRect = ceoBox.getBoundingClientRect();

      // 0. Draw line from Owner to CEO (L-shaped: vertical + horizontal)
      if (ownerBox) {
        const ownerRect = ownerBox.getBoundingClientRect();
        const ownerCenterX = toSvgCoord(ownerRect.left + ownerRect.width / 2 - containerRect.left);
        const ownerBottomY = toSvgCoord(ownerRect.bottom - containerRect.top);
        const ceoCenterX = toSvgCoord(ceoRect.left + ceoRect.width / 2 - containerRect.left);
        const ceoTopY = toSvgCoord(ceoRect.top - containerRect.top);

        // Midpoint Y for the horizontal segment
        const midY = ownerBottomY + (ceoTopY - ownerBottomY) * 0.5;

        // 1. Vertical line from Owner down to midpoint
        newLines.push({
          fromX: ownerCenterX,
          fromY: ownerBottomY,
          toX: ownerCenterX,
          toY: midY,
          color: '#1e293b',
          level: -2 // Special: always vertical
        });

        // 2. Horizontal line from Owner's X to CEO's X
        newLines.push({
          fromX: ownerCenterX,
          fromY: midY,
          toX: ceoCenterX,
          toY: midY,
          color: '#1e293b',
          level: -3 // Special: always horizontal
        });

        // 3. Vertical line down to CEO
        newLines.push({
          fromX: ceoCenterX,
          fromY: midY,
          toX: ceoCenterX,
          toY: ceoTopY,
          color: '#1e293b',
          level: -2 // Special: always vertical
        });

        // 4. Draw connector from Owner to Department 21
        const dept21 = container.querySelector('[data-dept-num="21"]');
        if (dept21) {
          const dept21Rect = dept21.getBoundingClientRect();
          const dept21CenterX = toSvgCoord(dept21Rect.left + dept21Rect.width / 2 - containerRect.left);
          const dept21TopY = toSvgCoord(dept21Rect.top - containerRect.top);

          // Position horizontal segment 80% of the way down (closer to dept 19)
          const horizontalY = ownerBottomY + (dept21TopY - ownerBottomY) * 0.85;

          // 1. Vertical line from Owner bottom down to horizontal level
          newLines.push({
            fromX: ownerCenterX,
            fromY: ownerBottomY,
            toX: ownerCenterX,
            toY: horizontalY,
            color: '#1e293b',
            level: -2
          });

          // 2. Horizontal line to Dept 19's X position
          newLines.push({
            fromX: ownerCenterX,
            fromY: horizontalY,
            toX: dept21CenterX,
            toY: horizontalY,
            color: '#1e293b',
            level: -3
          });

          // 3. Short vertical line down to Dept 19
          newLines.push({
            fromX: dept21CenterX,
            fromY: horizontalY,
            toX: dept21CenterX,
            toY: dept21TopY,
            color: '#1e293b',
            level: -2
          });
        }
      }

      // 1. Draw unified connector from CEO - single vertical line that branches to:
      //    a) CEO-managed divisions (horizontal bus ABOVE C-levels)
      //    b) C-levels (horizontal bus at C-level height)
      const clevelBoxes = container.querySelectorAll('.clevel-box');
      const ceoCenterX = toSvgCoord(ceoRect.left + ceoRect.width / 2 - containerRect.left);
      const ceoBottomY = toSvgCoord(ceoRect.bottom - containerRect.top);

      // Collect C-level positions
      const clevelPositions: { x: number; topY: number; bottomY: number }[] = [];
      clevelBoxes.forEach((clevelBox) => {
        const clevelRect = clevelBox.getBoundingClientRect();
        clevelPositions.push({
          x: toSvgCoord(clevelRect.left + clevelRect.width / 2 - containerRect.left),
          topY: toSvgCoord(clevelRect.top - containerRect.top),
          bottomY: toSvgCoord(clevelRect.bottom - containerRect.top)
        });
      });

      // Collect CEO-managed division positions
      const ceoManagedDivisions: { x: number; topY: number }[] = [];
      if (ceoFunc?.managesDivisions && ceoFunc.managesDivisions.length > 0) {
        ceoFunc.managesDivisions.forEach((divKey) => {
          const divHeader = container.querySelector(`[data-div-key="${divKey}"]`);
          if (divHeader) {
            const divRect = divHeader.getBoundingClientRect();
            ceoManagedDivisions.push({
              x: toSvgCoord(divRect.left + divRect.width / 2 - containerRect.left),
              topY: toSvgCoord(divRect.top - containerRect.top)
            });
          }
        });
      }

      const hasClevelConnectors = clevelPositions.length > 0;
      const hasDivisionConnectors = ceoManagedDivisions.length > 0;

      if (hasClevelConnectors || hasDivisionConnectors) {
        // Calculate bus Y positions
        const minClevelTopY = hasClevelConnectors
          ? Math.min(...clevelPositions.map(p => p.topY))
          : ceoBottomY + 100;

        // C-level bus positioned closer to C-levels
        const clevelBusY = hasClevelConnectors
          ? ceoBottomY + (minClevelTopY - ceoBottomY) * 0.7
          : 0;

        // Division bus should be ABOVE the C-level bus (closer to CEO)
        const divisionBusY = hasDivisionConnectors
          ? ceoBottomY + (minClevelTopY - ceoBottomY) * 0.3
          : 0;

        // Determine how far down the main vertical line should go
        const verticalEndY = hasClevelConnectors ? clevelBusY : divisionBusY;

        // 1. Draw single vertical line from CEO down
        newLines.push({
          fromX: ceoCenterX,
          fromY: ceoBottomY,
          toX: ceoCenterX,
          toY: verticalEndY,
          color: '#1e293b',
          level: -2
        });

        // 2a. CEO-managed divisions horizontal bus and drops (ABOVE C-levels)
        if (hasDivisionConnectors) {
          const allDivX = [ceoCenterX, ...ceoManagedDivisions.map(d => d.x)];
          const minDivX = Math.min(...allDivX);
          const maxDivX = Math.max(...allDivX);

          // Horizontal bus to divisions (above C-levels)
          newLines.push({
            fromX: minDivX,
            fromY: divisionBusY,
            toX: maxDivX,
            toY: divisionBusY,
            color: '#1e293b',
            level: -3
          });

          // Vertical drops to each division
          ceoManagedDivisions.forEach((div) => {
            newLines.push({
              fromX: div.x,
              fromY: divisionBusY,
              toX: div.x,
              toY: div.topY,
              color: '#1e293b',
              level: -2
            });
          });
        }

        // 2b. C-level horizontal bus and drops
        if (hasClevelConnectors) {
          const allClevelX = [ceoCenterX, ...clevelPositions.map(p => p.x)];
          const minClevelX = Math.min(...allClevelX);
          const maxClevelX = Math.max(...allClevelX);

          // Horizontal bus to C-levels
          newLines.push({
            fromX: minClevelX,
            fromY: clevelBusY,
            toX: maxClevelX,
            toY: clevelBusY,
            color: '#1e293b',
            level: -3
          });

          // Vertical drops to each C-level
          clevelPositions.forEach((pos) => {
            newLines.push({
              fromX: pos.x,
              fromY: clevelBusY,
              toX: pos.x,
              toY: pos.topY,
              color: '#1e293b',
              level: -2
            });
          });
        }
      }

      // 2. Draw lines from C-levels to their managed divisions
      const clevelItems = container.querySelectorAll('.clevel-item');
      clevelItems.forEach((clevelItem) => {
        const managedDivisionsAttr = clevelItem.getAttribute('data-manages-divisions');
        if (!managedDivisionsAttr) return;

        const managedDivisions = managedDivisionsAttr.split(',').filter(Boolean);
        const clevelBox = clevelItem.querySelector('.clevel-box');
        if (!clevelBox) return;

        const clevelRect = clevelBox.getBoundingClientRect();
        const clevelBottomY = toSvgCoord(clevelRect.bottom - containerRect.top);
        const clevelCenterX = toSvgCoord(clevelRect.left + clevelRect.width / 2 - containerRect.left);

        // Find all division headers this C-level manages
        const divisionTargets: { x: number; topY: number }[] = [];
        managedDivisions.forEach((divKey) => {
          const divHeader = container.querySelector(`[data-div-key="${divKey}"]`);
          if (divHeader) {
            const divRect = divHeader.getBoundingClientRect();
            divisionTargets.push({
              x: toSvgCoord(divRect.left + divRect.width / 2 - containerRect.left),
              topY: toSvgCoord(divRect.top - containerRect.top)
            });
          }
        });

        if (divisionTargets.length > 0) {
          // Find the min top Y of divisions to position bus line
          const minDivTopY = Math.min(...divisionTargets.map(d => d.topY));
          const divBusY = clevelBottomY + (minDivTopY - clevelBottomY) * 0.4;

          // Draw vertical line from C-level down to division bus
          newLines.push({
            fromX: clevelCenterX,
            fromY: clevelBottomY,
            toX: clevelCenterX,
            toY: divBusY,
            color: '#1e293b',
            level: -2
          });

          // Find horizontal extent of divisions
          const divXPositions = divisionTargets.map(d => d.x);
          const minDivX = Math.min(clevelCenterX, ...divXPositions);
          const maxDivX = Math.max(clevelCenterX, ...divXPositions);

          // Draw horizontal bus line to divisions
          newLines.push({
            fromX: minDivX,
            fromY: divBusY,
            toX: maxDivX,
            toY: divBusY,
            color: '#1e293b',
            level: -3
          });

          // Draw vertical drops to each division
          divisionTargets.forEach((div) => {
            newLines.push({
              fromX: div.x,
              fromY: divBusY,
              toX: div.x,
              toY: div.topY,
              color: '#1e293b',
              level: -2
            });
          });
        }
      });

      setLines(newLines);
    };

    // Calculate after a short delay to ensure DOM is ready
    const timer = setTimeout(calculateLines, 250);
    window.addEventListener('resize', calculateLines);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateLines);
    };
  }, [orgData, visibleDivisions, ceoFuncId, cLevelFuncIds, focusedDivision, zoom, divisionWidth]);

  return (
    <div ref={ref} className={`wise-board ${isFocusedMode ? 'focused-mode' : ''}`} style={{ '--text-scale': textSize / 100, '--division-width-scale': divisionWidth / 100, '--zoom-scale': zoom } as React.CSSProperties}>
      <div ref={containerRef} className="wise-board-container">
        {/* SVG Layer for Connection Lines - hidden in focused mode */}
        {!isFocusedMode && (
          <svg className="connection-lines" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 100 }}>
            <defs>
              {/* Subtle glow effect for connector lines */}
              <filter id="connectorGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
                <feFlood floodColor="#64748b" floodOpacity="0.3" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="shadow" />
                <feMerge>
                  <feMergeNode in="shadow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              {/* Gradient for connector lines */}
              <linearGradient id="connectorGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#334155" />
                <stop offset="100%" stopColor="#1e293b" />
              </linearGradient>
            </defs>
            {lines.map((line, idx) => {
              const isSpecialLine = line.level !== undefined && line.level < 0;
              const isHorizontalBus = line.level === -3;

              let pathD: string;
              if (isSpecialLine) {
                // Simple straight line for special cases (bus lines, drops)
                pathD = `M ${line.fromX} ${line.fromY} L ${line.toX} ${line.toY}`;
              } else {
                // Use D3 link generator for smooth bezier curves
                const source = { x: line.fromX, y: line.fromY };
                const target = { x: line.toX, y: line.toY };
                pathD = linkGenerator({ source, target }) || '';
              }

              const isVerticalDrop = line.level === -2;

              // Black color for connector lines
              const lineColor = '#1e293b';

              return (
                <g key={idx}>
                  {/* Shadow/background stroke for depth */}
                  <path
                    d={pathD}
                    stroke="#94a3b8"
                    strokeWidth={isHorizontalBus ? "4" : "5"}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.3"
                  />
                  {/* Main connector line */}
                  <path
                    d={pathD}
                    stroke={lineColor}
                    strokeWidth={isHorizontalBus ? "2" : "2.5"}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Junction circles at bus connection points */}
                  {isVerticalDrop && (
                    <circle cx={line.fromX} cy={line.fromY} r="4" fill={lineColor} stroke="#fff" strokeWidth="1" />
                  )}
                </g>
              );
            })}
          </svg>
        )}

        {/* Executive Hierarchy - hidden in focused mode */}
        {!isFocusedMode && (
          <div className="exec-section">
            {/* Owner Box - Above CEO */}
            {ownerFunc && (
              <div className="owner-row">
                <div
                  className={`exec-box owner-box ${editMode ? 'clickable' : ''}`}
                  data-exec-id={ownerFunc.id}
                  onClick={editMode ? (e) => handleEditClick(e, 'executive', 'executive' as DivisionKey, undefined, undefined, undefined, ownerFunc.id) : undefined}
                  title={editMode ? t('board.clickToEdit') : undefined}
                >
                  {ownerFunc.name || t('exec.owner')}{getAssignedName(ownerFunc.id) ? ` - ${getAssignedName(ownerFunc.id)}` : ''}
                  {editMode && <span className="exec-edit-hint">✏️</span>}
                </div>
              </div>
            )}

            {/* CEO Box - Below Owner */}
            {ceoFunc && (
              <div className="ceo-row">
                <div
                  className={`exec-box ceo-box ${editMode ? 'clickable' : ''}`}
                  data-exec-id={ceoFunc.id}
                  onClick={editMode ? (e) => handleEditClick(e, 'executive', 'executive' as DivisionKey, undefined, undefined, undefined, ceoFunc.id) : undefined}
                  title={editMode ? t('board.clickToEdit') : undefined}
                >
                  {ceoFunc.name || t('exec.ceo')}{getAssignedName(ceoFunc.id) ? ` - ${getAssignedName(ceoFunc.id)}` : ''}
                  {editMode && <span className="exec-edit-hint">✏️</span>}
                </div>
              </div>
            )}

            {/* C-Level Functions Row - Positioned above managed divisions */}
            {visibleCLevelFunctions.length > 0 && (
              <div className="clevel-row">
                {visibleCLevelFunctions.map((func) => {
                  // Get managed divisions for connector lines
                  const managedDivs = func.managesDivisions || [];
                  // Display function name with assigned employee if any
                  const assignedName = getAssignedName(func.id);
                  const displayLabel = assignedName
                    ? `${func.name} - ${assignedName}`
                    : func.name;

                  // Calculate position based on managed divisions
                  // Find indices of managed divisions in visibleDivisions array
                  const managedIndices = managedDivs
                    .map(divKey => visibleDivisions.findIndex(key => key === divKey))
                    .filter(idx => idx !== -1);

                  // Calculate center position as percentage from right (RTL)
                  let rightPercent = 50; // Default to center
                  if (managedIndices.length > 0) {
                    const minIdx = Math.min(...managedIndices);
                    const maxIdx = Math.max(...managedIndices);
                    const centerIdx = (minIdx + maxIdx) / 2;
                    // Position: center of the managed divisions span
                    rightPercent = ((centerIdx + 0.5) / visibleDivisions.length) * 100;
                  }

                  return (
                    <div
                      key={func.id}
                      className="clevel-item"
                      data-manages-divisions={managedDivs.join(',')}
                      style={{ right: `${rightPercent}%` }}
                    >
                      <div
                        className={`exec-box clevel-box ${editMode ? 'clickable' : ''}`}
                        data-exec-id={func.id}
                        onClick={editMode ? (e) => handleEditClick(e, 'executive', 'executive' as DivisionKey, undefined, undefined, undefined, func.id) : undefined}
                        title={editMode ? t('board.clickToEdit') : undefined}
                      >
                        {displayLabel}
                        {editMode && <span className="exec-edit-hint">✏️</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Reporting Lines Label */}
        <div className="reporting-section">
          <div className="reporting-line"></div>
        </div>

        {/* Division Headers Row */}
        <div className="wise-board-divisions-header" style={{ gridTemplateColumns: `repeat(${visibleDivisions.length}, 1fr)` }}>
          {visibleDivisions.map(key => {
            const division = orgData.divisions[key];
            const manager = getDivisionManager(key);
            const executiveFunc = getDivisionExecutiveFunc(key);

            const deptCount = division.departments?.length || 0;
            const canAddDept = deptCount < 4;
            const isEditing = editing?.type === 'division' && editing?.divisionKey === key;

            return (
              <div
                key={key}
                data-div-key={key}
                className={`division-header-cell ${editMode && !isEditing ? 'clickable' : ''} ${division.number === 1 || division.number === 5 ? 'elevated' : ''} ${isEditing ? 'editing' : ''}`}
                style={{ backgroundColor: division.color }}
                onClick={editMode && !isEditing ? (e) => handleEditClick(e, 'division', key) : undefined}
                title={editMode && !isEditing ? t('board.clickToEdit') : undefined}
              >
                {isEditing ? (
                  <DivisionInlineEdit
                    divisionKey={key}
                    division={division}
                    onClose={() => setEditing(null)}
                  />
                ) : (
                  <>
                    {/* Add department button - top left */}
                    {editMode && canAddDept && (
                      <button
                        className="add-dept-header-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(e, 'new-department', key);
                        }}
                        title={t('general.add')}
                      >
                        +
                      </button>
                    )}
                    <div className="division-reports-to">
                      {executiveFunc && (
                        <span className="reports-badge">{executiveFunc.name}</span>
                      )}
                    </div>
                    <div className="division-number">
                      <span className="division-icon">{division.icon}</span> {division.number}. {division.name}{manager && ` - ${manager.name}`}
                      {editMode && <span className="edit-hint">✏️</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Departments Grid */}
        <div className="wise-board-departments" style={{ gridTemplateColumns: `repeat(${visibleDivisions.length}, 1fr)` }}>
          {visibleDivisions.map(key => {
            const division = orgData.divisions[key];

            return (
              <div key={key} className="division-departments" style={{ '--division-color': division.color } as React.CSSProperties}>
                {/* Sort departments: Division 7 descending, all others ascending */}
                {/* Filter by focusedDepartment if set */}
                {[...(division.departments || [])]
                  .map((dept, idx) => ({ dept, originalIdx: idx })) // Keep original index
                  .filter(({ originalIdx }) => focusedDepartment === null || originalIdx === focusedDepartment)
                  .sort((a, b) => {
                    return division.number === 7
                      ? b.dept.number - a.dept.number  // Division 7: descending (21, 20, 19)
                      : a.dept.number - b.dept.number; // Others: ascending (1, 2, 3...)
                  }).map(({ dept, originalIdx: originalDeptIndex }) => {
                  const deptManagerName = getDeptManager(key, dept.number);
                  const isDeptEditing = editing?.type === 'department' && editing?.divisionKey === key && editing?.deptIndex === originalDeptIndex;

                  return (
                    <div key={dept.number} className={`department-cell ${isDeptEditing ? 'editing' : ''}`} data-div-key={key} data-dept-num={dept.number}>
                      {isDeptEditing ? (
                        <DepartmentInlineEdit
                          divisionKey={key}
                          deptIndex={originalDeptIndex}
                          divisionColor={division.color || '#667eea'}
                          onClose={() => setEditing(null)}
                        />
                      ) : (
                        <div
                          className={`dept-header-clickable ${editMode ? '' : 'non-editable'}`}
                          onClick={editMode ? (e) => handleEditClick(e, 'department', key, originalDeptIndex) : undefined}
                          title={editMode ? t('board.clickToEdit') : undefined}
                        >
                          <div className="dept-name-row">
                            <span className="dept-number-inline">{t('board.department')} {dept.number}</span>
                            <span className="dept-title">{dept.name}</span>
                            {deptManagerName && <span className="dept-manager-name"> - {deptManagerName}</span>}
                          </div>
                          {editMode && <span className="edit-hint-small">✏️</span>}
                        </div>
                      )}

                      {/* Sections (4-level hierarchy: Division → Department → Section → Function) */}
                      {showSections && !isDeptEditing && (
                      <div className="dept-sections">
                        {dept.sections.map((section, sectionIdx) => {
                          const sectionManager = section.managerId ? getEmployeeById(section.managerId) : null;
                          const totalSections = dept.sections.length;
                          const isSectionEditing = editing?.type === 'section' && editing?.divisionKey === key && editing?.deptIndex === originalDeptIndex && editing?.sectionIndex === sectionIdx;

                          return (
                            <div key={sectionIdx} className={`section-container ${isSectionEditing ? 'editing' : ''}`}>
                              {isSectionEditing ? (
                                <SectionInlineEdit
                                  divisionKey={key}
                                  deptIndex={originalDeptIndex}
                                  sectionIndex={sectionIdx}
                                  divisionColor={division.color || '#667eea'}
                                  onClose={() => setEditing(null)}
                                />
                              ) : (
                                <div
                                  className={`section-header ${editMode ? 'clickable' : ''}`}
                                  onClick={editMode ? (e) => handleEditClick(e, 'section', key, originalDeptIndex, sectionIdx) : undefined}
                                  title={editMode ? t('board.editSection') : undefined}
                                >
                                  {editMode && (
                                    <div className="section-reorder-btns">
                                      <button
                                        className="section-move-btn"
                                        onClick={(e) => { e.stopPropagation(); reorderSection(key, originalDeptIndex, sectionIdx, 'up'); }}
                                        disabled={sectionIdx === 0}
                                      >▲</button>
                                      <button
                                        className="section-move-btn"
                                        onClick={(e) => { e.stopPropagation(); reorderSection(key, originalDeptIndex, sectionIdx, 'down'); }}
                                        disabled={sectionIdx === totalSections - 1}
                                      >▼</button>
                                    </div>
                                  )}
                                  <div className="section-info">
                                    <span className="section-name">{section.name}</span>
                                    {sectionManager && <span className="section-manager"> - {sectionManager.name}</span>}
                                    {editMode && <span className="edit-hint-tiny">✏️</span>}
                                  </div>
                                </div>
                              )}

                              {/* Functions within section */}
                              {showFunctions && !isSectionEditing && (
                              <div className="section-functions">
                                {section.functions.map((func, funcIdx) => {
                                  const assignedEmployeeIds = func.id ? (orgData.assignments?.[func.id] || []) : [];
                                  const assignedEmployees = assignedEmployeeIds
                                    .map(empId => getEmployeeById(empId))
                                    .filter(Boolean);
                                  const totalFuncs = section.functions.length;
                                  const isFuncEditing = editing?.type === 'function' && editing?.divisionKey === key && editing?.deptIndex === originalDeptIndex && editing?.sectionIndex === sectionIdx && editing?.funcIndex === funcIdx;

                                  return (
                                    <div
                                      key={funcIdx}
                                      className={`function-item ${editMode ? 'clickable' : ''} ${isFuncEditing ? 'editing' : ''}`}
                                    >
                                      {isFuncEditing ? (
                                        <PostInlineEdit
                                          divisionKey={key}
                                          deptIndex={originalDeptIndex}
                                          sectionIndex={sectionIdx}
                                          funcIndex={funcIdx}
                                          divisionColor={division.color || '#667eea'}
                                          onClose={() => setEditing(null)}
                                        />
                                      ) : (
                                        <>
                                          {editMode && (
                                            <div className="function-reorder-btns">
                                              <button
                                                className="func-move-btn"
                                                onClick={(e) => { e.stopPropagation(); reorderFunction(key, originalDeptIndex, sectionIdx, funcIdx, 'up'); }}
                                                disabled={funcIdx === 0}
                                              >▲</button>
                                              <button
                                                className="func-move-btn"
                                                onClick={(e) => { e.stopPropagation(); reorderFunction(key, originalDeptIndex, sectionIdx, funcIdx, 'down'); }}
                                                disabled={funcIdx === totalFuncs - 1}
                                              >▼</button>
                                            </div>
                                          )}
                                          <div
                                            className="function-content"
                                            onClick={editMode ? (e) => handleEditClick(e, 'function', key, originalDeptIndex, sectionIdx, funcIdx) : undefined}
                                            title={editMode ? t('board.clickToEdit') : undefined}
                                          >
                                            <div className="function-name">
                                              {func.name}
                                              {assignedEmployees.length > 0 && (
                                                <span className="function-employee-name">
                                                  {' - '}{assignedEmployees.map(e => e!.name).join(', ')}
                                                </span>
                                              )}
                                              {editMode && <span className="edit-hint-tiny">✏️</span>}
                                            </div>
                                            {showFunctionDetails && func.objectives && func.objectives.length > 0 && (
                                              <ul className="function-objectives-list">
                                                {func.objectives.map((objective, objIdx) => (
                                                  <li key={objIdx}>{objective}</li>
                                                ))}
                                              </ul>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                                {/* Add function to section button */}
                                {editMode && (
                                  <button
                                    className="add-func-btn"
                                    onClick={(e) => handleEditClick(e, 'new-function', key, originalDeptIndex, sectionIdx)}
                                    title={t('board.addFunction')}
                                  >
                                    {t('board.addFunction')}
                                  </button>
                                )}
                              </div>
                              )}
                            </div>
                          );
                        })}
                        {/* Add new section button */}
                        {editMode && (
                          <button
                            className="add-section-btn"
                            onClick={(e) => handleEditClick(e, 'new-section', key, originalDeptIndex)}
                            title={t('board.addSection')}
                          >
                            {t('board.addSection')}
                          </button>
                        )}
                      </div>
                      )}

                      {/* Department VFP footer - hidden when editing */}
                      {dept.vfp && !isDeptEditing && (
                        <div className="dept-vfp-footer">
                          <div className="dept-vfp-label">{t('board.vfp')}</div>
                          <div className="dept-vfp-content">{dept.vfp}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Division VFPs Row */}
        <div className="wise-board-vfps" style={{ gridTemplateColumns: `repeat(${visibleDivisions.length}, 1fr)` }}>
          {visibleDivisions.map(key => {
            const division = orgData.divisions[key];

            return (
              <div key={key} className="division-vfp-cell" style={{ '--division-color': division.color } as React.CSSProperties}>
                <div className="vfp-label">{t('board.vfp')}</div>
                <div className="vfp-content">{division.vfp}</div>
              </div>
            );
          })}
        </div>

        {/* Company VFP at Bottom - hidden in focused mode */}
        {!isFocusedMode && orgData.companyVfp && (
          <div className="company-vfp-section">
            <div className="company-vfp-label">{t('board.vfp')}</div>
            <div className="company-vfp-content">{orgData.companyVfp}</div>
          </div>
        )}

        {/* Footer - Prepared by TurnKeyOS.AI for Client */}
        {!isFocusedMode && (
          <div className="orgboard-footer">
            <span className="footer-text">
              {language === 'en' ? 'Prepared by' : language === 'ar' ? 'أعدّ بواسطة' : 'הוכן על ידי'}
            </span>
            <img src="/turnkeyos-logo.svg" alt="TurnKeyOS.AI" className="footer-federman-logo" />
            <span className="footer-text">
              {language === 'en' ? 'for' : language === 'ar' ? 'لـ' : 'עבור'}
            </span>
            {organization?.logo ? (
              <img src={organization.logo} alt={organization.name} className="footer-client-logo" />
            ) : (
              <span className="footer-client-name">{organization?.name}</span>
            )}
          </div>
        )}
      </div>

      {/* Inline Edit Popover - only for types that don't use inline edit */}
      {editing && !['division', 'department', 'section', 'function'].includes(editing.type) && (
        <InlineEditPopover
          editing={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
});

WiseBoardView.displayName = 'WiseBoardView';
